import axios from 'axios';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Clear invalid token and redirect to login
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Helper function to check if user is authenticated
const isAuthenticated = () => {
  const token = localStorage.getItem('authToken');
  return !!token;
};

// Auth API
export const authAPI = {
  login: async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);
      // Store token and user data
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      return { success: true };
    } catch (error) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      throw error;
    }
  },

  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  isAuthenticated,
};

// Endpoints API
export const endpointsAPI = {
  getAll: async () => {
    try {
      if (!isAuthenticated()) {
        throw new Error('User not authenticated');
      }
      const response = await api.get('/endpoints');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/endpoints/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  create: async (endpoint) => {
    try {
      const response = await api.post('/endpoints', endpoint);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  update: async (id, endpoint) => {
    try {
      const response = await api.put(`/endpoints/${id}`, endpoint);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  delete: async (id) => {
    try {
      await api.delete(`/endpoints/${id}`);
      return { success: true };
    } catch (error) {
      throw error;
    }
  },

  testConnection: async (id) => {
    try {
      const response = await api.post(`/endpoints/${id}/test`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getStatus: async () => {
    try {
      if (!isAuthenticated()) {
        throw new Error('User not authenticated');
      }
      const response = await api.get('/endpoints/status');
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Users API
export const usersAPI = {
  getAll: async () => {
    try {
      const response = await api.get('/users');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  create: async (user) => {
    try {
      const response = await api.post('/users', user);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  update: async (id, user) => {
    try {
      const response = await api.put(`/users/${id}`, user);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  delete: async (id) => {
    try {
      await api.delete(`/users/${id}`);
      return { success: true };
    } catch (error) {
      throw error;
    }
  },
};

// Settings API
export const settingsAPI = {
  get: async () => {
    try {
      const response = await api.get('/settings');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getAll: async () => {
    try {
      const response = await api.get('/settings');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  update: async (settings) => {
    try {
      const response = await api.put('/settings', settings);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Activity Logs API
export const activityLogsAPI = {
  getAll: async (params) => {
    try {
      const response = await api.get('/activity-logs', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/activity-logs/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  export: async (params) => {
    try {
      const response = await api.get('/activity-logs/export', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// System API
export const systemAPI = {
  getHealth: async () => {
    try {
      const response = await api.get('/system/health');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getStats: async () => {
    try {
      const response = await api.get('/system/stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getVersion: async () => {
    try {
      const response = await api.get('/system/version');
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};